import axios from 'axios';

const API_URL =
  import.meta.env.VITE_APP_API_URL ||
  import.meta.env.VITE_API_URL ||
  'http://localhost:3001/api';

const SPRYMEETING_API_URL =
  import.meta.env.VITE_SPRYMEETING_API_URL ||
  'http://localhost:3002/api';

const DEFAULT_SPRYMEETING_APP_URL =
  import.meta.env.VITE_SPRYMEETING_URL ||
  'http://localhost:5179';

let accessToken: string | null = null;

type ApiResponseLike = {
  data?: any;
};

type SpryMeetingProfileResponse = {
  workspaceId?: string | null;
  runtime?: {
    appBaseUrl?: string | null;
  };
};

type SpryMeetingDocumentRoomResponse = {
  meeting?: {
    id?: string | null;
  };
  inviteLink?: string | null;
};

type LaunchDocumentMeetingInput = {
  docId: string;
  docType: string;
  workspaceId?: string | null;
  source: string;
  entryPoint: string;
  returnUrl?: string;
};

type LaunchDocumentMeetingResult = {
  meetingId: string;
  workspaceId: string;
  url: string;
  inviteLink: string | null;
};

export type NormalizedBoardSummary = {
  id: string;
  title: string;
  driveFileId?: string | null;
  updatedAt?: string;
  createdAt?: string;
  owner?: { name?: string; email?: string } | null;
  [key: string]: any;
};

export type NormalizedBoardDetail = {
  id?: string;
  title?: string;
  driveFileId?: string | null;
  data?: any;
  canEdit?: boolean;
  accessRole?: string | null;
  [key: string]: any;
};

export type NormalizedSpryboardFileLoad = {
  fileId?: string;
  filename?: string;
  boardId?: string | null;
  boardData?: any;
  lastModified?: string;
  workspaceId?: string | null;
  canEdit?: boolean;
  accessRole?: string | null;
  [key: string]: any;
};

export type NormalizedSpryboardSave = {
  fileId?: string;
  filename?: string;
  boardId?: string | null;
  savedAt?: string;
  workspaceId?: string | null;
  [key: string]: any;
};

export const extractBoardFromResponse = (
  response: ApiResponseLike,
): NormalizedBoardDetail | null => {
  const payload = response?.data;
  const board =
    payload?.board ??
    payload?.data?.board ??
    payload?.data ??
    payload;

  if (!board || typeof board !== 'object') {
    return null;
  }

  return board as NormalizedBoardDetail;
};

export const extractBoardsFromResponse = (
  response: ApiResponseLike,
): NormalizedBoardSummary[] => {
  const payload = response?.data;
  const candidates = [
    payload?.boards,
    payload?.data?.boards,
    payload?.data,
    payload,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as NormalizedBoardSummary[];
    }
  }
  return [];
};

export const extractSpryboardLoadFromResponse = (
  response: ApiResponseLike,
): NormalizedSpryboardFileLoad => {
  const payload = response?.data;
  const data = payload?.data ?? payload;
  return (data && typeof data === 'object' ? data : {}) as NormalizedSpryboardFileLoad;
};

export const extractSpryboardSaveFromResponse = (
  response: ApiResponseLike,
): NormalizedSpryboardSave => {
  const payload = response?.data;
  const data = payload?.data ?? payload;
  return (data && typeof data === 'object' ? data : {}) as NormalizedSpryboardSave;
};

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

const getOidcAccessTokenFromStorage = (): string | null => {
  if (typeof window === 'undefined') return null;

  const keycloakUrl = import.meta.env.VITE_APP_KEYCLOAK_URL || import.meta.env.VITE_KEYCLOAK_URL;
  const keycloakRealm = import.meta.env.VITE_APP_KEYCLOAK_REALM || import.meta.env.VITE_KEYCLOAK_REALM;
  const keycloakClientId =
    import.meta.env.VITE_APP_KEYCLOAK_CLIENT_ID ||
    import.meta.env.VITE_KEYCLOAK_CLIENT_ID ||
    'spryboard-spa';

  const keysToCheck: string[] = [];
  if (keycloakUrl && keycloakRealm) {
    keysToCheck.push(`oidc.user:${keycloakUrl}/realms/${keycloakRealm}:${keycloakClientId}`);
  }

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key && key.startsWith('oidc.user:') && !keysToCheck.includes(key)) {
      keysToCheck.push(key);
    }
  }

  for (const key of keysToCheck) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { access_token?: string };
      if (parsed?.access_token) return parsed.access_token;
    } catch {
      // Ignore malformed storage values.
    }
  }

  return null;
};

const getWorkspaceIdFromLocation = (): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('workspaceId');
};

const ensureTrailingSlash = (value: string) => (value.endsWith('/') ? value : `${value}/`);

const resolveAccessToken = () => accessToken || getOidcAccessTokenFromStorage();

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = resolveAccessToken();
    if (token) {
      accessToken = token;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for auth failures
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as {
      _retry?: boolean;
      _retryWithHydratedToken?: boolean;
      headers?: Record<string, string>;
      [key: string]: any;
    };

    // Handle network errors gracefully (backend offline)
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.warn('⚠️ Backend server is offline - working in local-only mode');
      return Promise.reject(new Error('BACKEND_OFFLINE'));
    }

    if (error.response?.status === 401 && !originalRequest?._retryWithHydratedToken) {
      const hydratedToken = getOidcAccessTokenFromStorage();
      const existingAuthHeader =
        originalRequest?.headers?.Authorization ||
        originalRequest?.headers?.authorization;
      if (hydratedToken && (!existingAuthHeader || hydratedToken !== accessToken)) {
        accessToken = hydratedToken;
        originalRequest._retryWithHydratedToken = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${hydratedToken}`;
        return api(originalRequest);
      }
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      window.dispatchEvent(
        new CustomEvent('spry:auth-required', {
          detail: {
            status: 401,
            reason: error.response?.data?.error || 'Unauthorized',
          },
        }),
      );
    }

    return Promise.reject(error);
  },
);

const buildMeetingPrejoinUrl = (
  meetingId: string,
  workspaceId: string,
  appBaseUrl: string,
  options: LaunchDocumentMeetingInput,
) => {
  const url = new URL(`room/${meetingId}/prejoin`, ensureTrailingSlash(appBaseUrl || DEFAULT_SPRYMEETING_APP_URL));
  url.searchParams.set('workspaceId', workspaceId);
  url.searchParams.set('source', options.source);
  url.searchParams.set('entryPoint', options.entryPoint);
  url.searchParams.set('create', 'document-room');
  url.searchParams.set('docId', options.docId);
  url.searchParams.set('docType', options.docType);
  if (options.returnUrl) {
    url.searchParams.set('returnUrl', options.returnUrl);
  }
  return url.toString();
};

const resolveSpryMeetingWorkspace = async (workspaceId?: string | null) => {
  const preferredWorkspaceId = workspaceId || getWorkspaceIdFromLocation();
  const response = await api.get<SpryMeetingProfileResponse>('/me', {
    baseURL: SPRYMEETING_API_URL,
    headers: preferredWorkspaceId ? { 'x-workspace-id': preferredWorkspaceId } : undefined,
  });

  const resolvedWorkspaceId = response.data.workspaceId || preferredWorkspaceId;
  if (!resolvedWorkspaceId) {
    throw new Error('No active SpryMeeting workspace was found. Open the board from SpryWorkspace first.');
  }

  return {
    workspaceId: resolvedWorkspaceId,
    appBaseUrl: response.data.runtime?.appBaseUrl || DEFAULT_SPRYMEETING_APP_URL,
  };
};

// =============================================================================
// Auth API
// =============================================================================
export const authAPI = {
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// =============================================================================
// Board API (CRUD + sharing)
// =============================================================================
export const boardAPI = {
  createBoard: (data: { title: string; data?: any; isPublic?: boolean }) =>
    api.post('/boards', data),

  getBoards: () => api.get('/boards'),

  getBoard: (id: string, options?: { useDrive?: boolean }) =>
    api.get(
      `/boards/${id}`,
      options?.useDrive ? { params: { useDrive: true } } : undefined,
    ),

  updateBoard: (
    id: string,
    data: {
      title?: string;
      data?: any;
      starred?: boolean;
      driveFileId?: string;
    },
  ) => api.patch(`/boards/${id}`, data),

  deleteBoard: (id: string) => api.delete(`/boards/${id}`),

  duplicateBoard: (id: string, title?: string) =>
    api.post(`/boards/${id}/duplicate`, { title }),

  // Sharing
  shareBoard: (id: string, data: { email: string; role: string }) =>
    api.post(`/boards/${id}/share`, data),

  getPermissions: (id: string) => api.get(`/boards/${id}/permissions`),

  updatePermission: (id: string, userId: string, role: string) =>
    api.patch(`/boards/${id}/permissions/${userId}`, { role }),

  removePermission: (id: string, userId: string) =>
    api.delete(`/boards/${id}/permissions/${userId}`),

  updatePublicAccess: (id: string, isPublic: boolean) =>
    api.patch(`/boards/${id}/public-access`, { isPublic }),

  getSharedWithMe: () => api.get('/boards/shared-with-me'),
};

// =============================================================================
// SpryBoard Drive API (file-level operations)
// =============================================================================
export const spryboardAPI = {
  save: (data: {
    filename: string;
    boardData: any;
    parentId?: string;
    fileId?: string;
    boardId?: string;
  }) => api.post('/spryboard/save', data),

  listFiles: (parentId?: string) =>
    api.get('/spryboard/files', parentId ? { params: { parentId } } : undefined),

  load: (fileId: string) => api.get(`/spryboard/${fileId}`),

  autoSave: (fileId: string, data: { boardData: any; filename?: string }) =>
    api.patch(`/spryboard/${fileId}`, data),
};

// =============================================================================
// Drive API (generic SpryDrive operations)
// =============================================================================
export const driveAPI = {
  listFiles: () => api.get('/drive/files'),

  getFile: (fileId: string) => api.get(`/drive/files/${fileId}`),

  downloadFile: (fileId: string) =>
    api.get(`/drive/files/${fileId}/download`, { responseType: 'blob' }),

  uploadFile: (file: File, parentId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId) {
      formData.append('parentId', parentId);
    }
    return api.post('/drive/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const sprymeetingAPI = {
  launchDocumentRoom: async (options: LaunchDocumentMeetingInput): Promise<LaunchDocumentMeetingResult> => {
    const { workspaceId, appBaseUrl } = await resolveSpryMeetingWorkspace(options.workspaceId);
    const response = await api.post<SpryMeetingDocumentRoomResponse>(
      '/meetings/document-room',
      {
        docId: options.docId,
        docType: options.docType,
        workspaceId,
        entryPoint: options.entryPoint,
        returnUrl: options.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : null),
      },
      {
        baseURL: SPRYMEETING_API_URL,
        headers: {
          'x-workspace-id': workspaceId,
        },
      },
    );

    const meetingId = response.data.meeting?.id;
    if (!meetingId) {
      throw new Error('SpryMeeting did not return a meeting room.');
    }

    return {
      meetingId,
      workspaceId,
      inviteLink: response.data.inviteLink ?? null,
      url: buildMeetingPrejoinUrl(meetingId, workspaceId, appBaseUrl, options),
    };
  },
};

export default api;
